import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import PermissionModal from '../modals/PermissionModal';
import PageHeader from '../common/PageHeader';
import Card from '../common/Card';
import Button from '../common/Button';
import Badge from '../common/Badge';
import { CardSkeleton } from '../common/Skeleton';

const RolesPage = () => {
  const { isAdmin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAdmin) {
      fetchRoles();
    }
  }, [isAdmin]);

  const fetchRoles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/roles');
      
      // Filter out Admin role - only show Manager and Staff
      const filteredRoles = response.data.roles.filter(
        role => role.name.toLowerCase() !== 'admin'
      );
      
      setRoles(filteredRoles);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to load roles');
      console.error('[ROLES PAGE] Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (role) => {
    setSelectedRole(role);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedRole(null);
  };

  const handlePermissionsUpdated = async () => {
    // Refresh roles after permissions are updated
    await fetchRoles();
  };

  // Map module names from API to display format
  const getModulePermissions = (permissions, moduleKey) => {
    const moduleMap = {
      dailyBusiness: 'daily_business',
      stockCheck: 'stock_check',
      literStatement: 'liter_statement'
    };
    
    const apiKey = moduleMap[moduleKey] || moduleKey;
    return permissions[apiKey] || [];
  };

  // Map permission names to display names
  const getPermissionDisplayName = (perm) => {
    const map = {
      create: 'Create',
      view: 'Read',
      edit: 'Update',
      delete: 'Delete'
    };
    return map[perm] || perm;
  };

  // Map module keys to display names
  const moduleDisplayMap = {
    dailyBusiness: 'Daily Business',
    stockCheck: 'Stock Check',
    literStatement: 'Liter Statement'
  };

  const moduleKeys = ['dailyBusiness', 'stockCheck', 'literStatement'];

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-2 sm:px-3">
        <Card className="!border-red-200 dark:!border-red-500/30 bg-red-50 dark:bg-red-500/10" padding="sm">
          <p className="text-red-600 dark:text-red-400 font-medium text-sm">
            <i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
            Access denied. Admin privileges required.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-3">
      <PageHeader
        icon="fa-user-tag"
        title="Role Management"
        subtitle="Configure access permissions for different roles"
      />

      {error && (
        <Card className="!border-red-200 dark:!border-red-500/30 bg-red-50 dark:bg-red-500/10 mb-6" padding="sm">
          <p className="text-red-600 dark:text-red-400 font-medium text-sm" role="alert">
            <i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
            {error}
          </p>
        </Card>
      )}

      {loading ? (
        <CardSkeleton count={2} className="sm:grid-cols-2 lg:grid-cols-2" />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {roles.map((role) => {
              // Convert API permissions format to display format
              const displayPermissions = {
                dailyBusiness: getModulePermissions(role.permissions, 'dailyBusiness'),
                stockCheck: getModulePermissions(role.permissions, 'stockCheck'),
                literStatement: getModulePermissions(role.permissions, 'literStatement')
              };

              return (
                <Card key={role.id} hover>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                        role.name === 'Manager'
                          ? 'bg-gradient-to-br from-primary to-primary-dark'
                          : 'bg-gradient-to-br from-slate-500 to-slate-600'
                      }`}>
                        <i className="fas fa-user-shield text-white text-xl" aria-hidden="true"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{role.name}</h3>
                    </div>
                    <Button size="sm" icon="fa-edit" onClick={() => handleEditClick(role)} aria-label={`Edit ${role.name} permissions`}>
                      Edit
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {moduleKeys.map((moduleKey) => {
                      const permissions = displayPermissions[moduleKey] || [];
                      const moduleName = moduleDisplayMap[moduleKey];
                      const iconMap = {
                        dailyBusiness: 'fas fa-clipboard-check text-primary',
                        stockCheck: 'fas fa-boxes text-green-500',
                        literStatement: 'fas fa-file-alt text-yellow-500'
                      };
                      const badgeColor = {
                        dailyBusiness: 'primary',
                        stockCheck: 'success',
                        literStatement: 'warning'
                      };

                      return (
                        <div key={moduleKey}>
                          <h4 className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2">
                            <i className={iconMap[moduleKey]} aria-hidden="true"></i>
                            {moduleName}
                          </h4>
                          <div className="flex gap-2 flex-wrap">
                            {permissions.length > 0 ? (
                              permissions.map((perm) => (
                                <Badge key={perm} color={badgeColor[moduleKey]}>
                                  {getPermissionDisplayName(perm)}
                                </Badge>
                              ))
                            ) : (
                              <Badge color="gray">No permissions</Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>

          {showModal && selectedRole && (
            <PermissionModal
              role={selectedRole}
              onClose={handleModalClose}
              onUpdate={handlePermissionsUpdated}
            />
          )}
        </>
      )}
    </div>
  );
};

export default RolesPage;
